import * as React from 'react'
import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  useScanAndFetchStandards,
  useExportTreeStandardsToSheet,
  useTreeSummary,
} from './hooks'
import {
  nodesFromTreeAtom,
  standardIdMapAtom,
  refreshCounterAtom,
} from './atoms'
import { SummaryTable } from '../../SummaryTable'
import { OpenJsonButton } from '../../utility/OpenJsonButton'
import { DownloadJsonButton } from '../../utility/DownloadJsonButton'
import { useGetTreeById } from '../../../reducers/trees_hooks'
import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import styled from '@emotion/styled'

const SectionPaper = styled(Paper)(() => ({
  padding: '24px',
  marginBottom: '24px',
}))

interface ExportToGoogleSheetProps {
  treeId: string
  versionId: string
}

export function ExportToGoogleSheet({
  treeId,
  versionId,
}: ExportToGoogleSheetProps) {
  const { loading, error } = useScanAndFetchStandards(treeId, versionId)
  const nodesFromTree = useAtomValue(nodesFromTreeAtom)
  const refreshCounter = useAtomValue(refreshCounterAtom)
  const syncStarted = refreshCounter > 0
  const { tree } = useGetTreeById({ treeId, versionId })
  const guser = useAtomValue(googleUserAtom)
  const standardIdMap = useAtomValue(standardIdMapAtom)
  const [filterSkillsNodesOnly, setFilterSkillsNodesOnly] = useState(false)
  const {
    exportToSheet,
    loading: loadingExport,
    spreadsheetId,
  } = useExportTreeStandardsToSheet()
  const summaryRows = useTreeSummary()

  const handleExport = () => {
    if (!tree || !guser.credential?.accessToken) {
      return
    }
    exportToSheet(
      tree,
      guser.credential.accessToken,
      standardIdMap,
      filterSkillsNodesOnly,
    )
  }

  const isLoggedIn = !!(guser && guser.user && guser.credential?.accessToken)

  const spreadsheetUrl = spreadsheetId
    ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`
    : null

  return (
    <SectionPaper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">Export tree to Google Sheet</Typography>
        <OpenJsonButton
          data={nodesFromTree}
          disabled={nodesFromTree.length === 0}
          buttonText="Open JSON"
        />
        <DownloadJsonButton
          data={nodesFromTree}
          filename={`nodes_from_tree_${treeId}_${versionId}.json`}
          disabled={nodesFromTree.length === 0}
        />
      </Box>
      {loading ? (
        <Box>Scanning tree and fetching standards...</Box>
      ) : error ? (
        <Box color="error">Error: {error}</Box>
      ) : (
        <Box>
          <SummaryTable rows={summaryRows} />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterSkillsNodesOnly}
                  onChange={(e) => setFilterSkillsNodesOnly(e.target.checked)}
                  size="small"
                />
              }
              label="Export only nodes with skills (do not select if cascade)"
              sx={{
                fontSize: '0.75rem',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.75rem',
                },
              }}
            />
            <Link
              href="/standards_tools/search_kiddom_standards"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: '0.875rem' }}
            >
              Search standards by state/subject
            </Link>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
            {!isLoggedIn ? (
              <Box
                sx={{
                  '& button': {
                    textTransform: 'uppercase',
                    size: 'medium',
                  },
                }}
              >
                <GoogleLoginButton hideWhenLoggedIn={false} />
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleExport}
                disabled={
                  loadingExport ||
                  !tree ||
                  !guser.credential?.accessToken ||
                  syncStarted
                }
                sx={{ textTransform: 'uppercase' }}
              >
                {loadingExport ? 'Exporting...' : 'Export'}
              </Button>
            )}
            {syncStarted && (
              <Typography
                variant="body2"
                sx={{
                  color: '#d97706',
                  fontWeight: 600,
                  fontStyle: 'italic',
                  ml: 2,
                  px: 2,
                  py: 1,
                  backgroundColor: '#fef3c7',
                  borderRadius: 1,
                  border: '1px solid #fbbf24',
                }}
              >
                ⚠️ Refresh Page to Reload Tree with Latest Changes
              </Typography>
            )}
          </Box>
          {spreadsheetUrl && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                <Link
                  href={spreadsheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}
                >
                  {spreadsheetUrl}
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </SectionPaper>
  )
}
